import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function BillingSuccess() {
  const { t } = useLang();
  const { appId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", payment_status: "pending", amount: 0, currency: "eur", attempts: 0 });

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error" }); return; }
    let cancelled = false;
    const poll = async (attempts) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/${appId}/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", ...data, attempts });
          return;
        }
        if (attempts >= 8) {
          setState({ phase: "timeout", ...data, attempts });
          return;
        }
        setState({ phase: "checking", ...data, attempts });
        setTimeout(() => poll(attempts + 1), 2500);
      } catch {
        setState((s) => ({ ...s, phase: "error" }));
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, appId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-50 via-white to-wine-50/40">
      <div className="mx-auto max-w-2xl px-6 py-10">
        <Logo />
        <div className="mt-12 rounded-3xl border border-wine-100 bg-white p-10 text-center" data-testid="billing-success-card">
          {state.phase === "checking" && (
            <>
              <Loader2 className="mx-auto h-16 w-16 animate-spin text-wine-500" />
              <h1 className="mt-5 text-3xl font-bold text-navy">{t("comp_billingsuccess_90")}</h1>
              <p className="mt-3 text-neutral-600">Vérification auprès de Stripe (essai {state.attempts + 1}/8).</p>
            </>
          )}
          {state.phase === "paid" && (
            <>
              <CheckCircle className="mx-auto h-16 w-16 text-emerald-500" />
              <h1 className="mt-5 text-4xl font-bold text-navy">{t("comp_billingsuccess_91")} <span className="font-display italic text-wine-600" style={{ fontWeight: 500 }}>{t("comp_billingsuccess_92")}</span> 🍷</h1>
              <p className="mt-3 text-neutral-600">{t("comp_billingsuccess_93")} <span className="font-bold text-wine-700">{state.amount} {state.currency?.toUpperCase()}</span> {t("comp_billingsuccess_94")}</p>
              <Link to="/login" className="mt-6 inline-block rounded-full bg-wine-500 px-7 py-3 text-sm font-semibold text-white hover:bg-wine-600" data-testid="success-to-login">{t("comp_billingsuccess_95")}</Link>
            </>
          )}
          {state.phase === "timeout" && (
            <>
              <AlertCircle className="mx-auto h-16 w-16 text-amber-500" />
              <h1 className="mt-5 text-3xl font-bold text-navy">Paiement en cours</h1>
              <p className="mt-3 text-neutral-600">{t("comp_billingsuccess_96")}</p>
            </>
          )}
          {state.phase === "error" && (
            <>
              <AlertCircle className="mx-auto h-16 w-16 text-red-500" />
              <h1 className="mt-5 text-3xl font-bold text-navy">{t("comp_billingsuccess_97")}</h1>
              <p className="mt-3 text-neutral-600">{t("comp_billingsuccess_98")}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
